/**
 * @doc-source /cmsApp/shared/DocumentationRenderer/schemas/cms-plugin-loader.docs.json
 */

(function () {
    /*----------------------------------------------------------------------
        CMS Plugin Loader (overview)
        - Fetches plugin metadata from backend
        - Registers plugins and dependency graph
        - Loads assets in bundle or individual mode
        - Exposes public API on window.__cmsSystem
    ----------------------------------------------------------------------*/
    /*----------------------------------------------------------------------
        Section: Loader state
    ----------------------------------------------------------------------*/
    window.__cmsPluginsReady = window.__cmsPluginsReady || new Map();
    const loadedJs = new Set();
    const loadedCss = new Set();
    const loadingCss = new Map();
    const preloadedJs = new Set();
    const preloadedCss = new Set();
    const __cmsPluginRegistry = {};
    let APP_CONTEXT = 'backend';
    const publicJsSkip = new Set();
    let publicJsSkipPromise = null;
    const bundleManifest = { data: null, promise: null };
    const globalShimsState = { loaded: false, list: [], keys: new Set() };

    /*----------------------------------------------------------------------
        Function: fetchPlugins
    ----------------------------------------------------------------------*/
    async function fetchPlugins() {
        try {
            // Ensure skip list is loaded before we start registering assets.
            await loadPublicJsExternals();

            const metaList = await window.cmsGlobal.fetchJson(window.cmsRoutes.apiListPlugins, {
                lCheckSession: false,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Cms-Internal-Token': window.cmsInternalToken
                },
                cache: 'no-cache',
                credentials: 'same-origin',
                body: JSON.stringify({ cms_internal: '1' })
            });

            const assertMeta = (obj) => {
                if (!obj || typeof obj !== 'object') throw new Error('Invalid plugin meta');
                ['root', 'route', 'namespace'].forEach((k) => {
                    if (!obj[k]) throw new Error(`Missing ${k}`);
                });
            };

            const resolveMetaAssetPath = (assetPath, namespace) => {
                if (typeof assetPath !== 'string') return undefined;
                const trimmed = assetPath.trim();
                if (!trimmed) return undefined;

                // Preserve explicit absolute, traversal, and URL-style paths from meta.
                if (trimmed.startsWith('/') || trimmed.startsWith('./') || trimmed.startsWith('../') || /^[a-z]+:/i.test(trimmed)) {
                    return trimmed;
                }

                return `${namespace}/${trimmed}`;
            };

            // Normalize every meta entry into the internal registry format.'
            metaList.forEach((obj) => {
                assertMeta(obj);
                const { root, route, namespace, js, css, selfInit, depends, dependsSelf, intent, frontend_depends } = obj;
                const publishName = `${root}_${route}`;

                registerCmsPlugin(namespace, {});
                const entry = __cmsPluginRegistry[namespace];

                entry.namespace = namespace;
                entry.publishName = publishName;
                entry.selfInit = !!selfInit;
                entry.hasIntents = !!intent;
                entry.intents = intent;
                entry.self = { css: [], js: [] };

                // In frontend context, allow frontend_depends to override css/js.
                let lLoadCssJs = true;
                if (frontend_depends && APP_CONTEXT === 'frontend') {
                    lLoadCssJs = false;
                    if (Array.isArray(frontend_depends?.css)) frontend_depends.css.forEach((p) => entry.self.css.push(p));
                    if (Array.isArray(frontend_depends?.js)) frontend_depends.js.forEach((p) => entry.self.js.push(p));
                }

                if (lLoadCssJs) {
                    entry.js = resolveMetaAssetPath(js, namespace);
                    entry.css = resolveMetaAssetPath(css, namespace);
                    if (Array.isArray(dependsSelf?.css)) {
                        dependsSelf.css.forEach((p) => {
                            const resolved = resolveMetaAssetPath(p, namespace);
                            if (resolved) entry.self.css.push(resolved);
                        });
                    }
                    if (Array.isArray(dependsSelf?.js)) {
                        dependsSelf.js.forEach((p) => {
                            const resolved = resolveMetaAssetPath(p, namespace);

                            if (resolved) entry.self.js.push(resolved);
                        });
                    }
                }

                // Precompute bundle path so bundle mode can skip individual file loading.
                entry.bundleJs = deriveBundlePath(namespace, obj);
                entry.depends = Array.isArray(depends) ? depends.slice() : undefined;
                entry.loading = null;
            });
        } catch (err) {
            window.cmsGlobal.toastError(`cmsPluginLoader::fetchPlugins(): ${err.message}`);
        }
    }

    /*----------------------------------------------------------------------
        Section: Asset helpers
    ----------------------------------------------------------------------*/
    const trustedTypesPolicy =
        window.trustedTypes && typeof window.trustedTypes.createPolicy === 'function'
            ? window.trustedTypes.createPolicy('cms-plugin-loader', {
                createScriptURL: (value) => value
            })
            : null;

    /*----------------------------------------------------------------------
        Function: withBuildVersion
    ----------------------------------------------------------------------*/
    function withBuildVersion(fullPath) {
        // Force reload when build version changes; default to 1 for dev.
        return `${fullPath}?v=${window.__BUILD_VERSION__ || '1'}`;
    }

    /*----------------------------------------------------------------------
        Function: applyScriptNonce
    ----------------------------------------------------------------------*/
    function applyScriptNonce(script) {
        const nonce = typeof window.__CSP_NONCE__ === 'string' ? window.__CSP_NONCE__.trim() : '';

        if (nonce) {
            script.nonce = nonce;
        }
    }

    /*----------------------------------------------------------------------
        Function: toTrustedScriptURL
    ----------------------------------------------------------------------*/
    function toTrustedScriptURL(value) {
        const rawUrl = String(value || '').trim();

        if (!rawUrl) {
            throw new Error('Empty script URL.');
        }

        const parsedUrl = new URL(rawUrl, window.location.origin);

        if (parsedUrl.origin !== window.location.origin) {
            throw new Error(`Blocked cross-origin script URL: ${rawUrl}`);
        }

        if (!parsedUrl.pathname.startsWith('/cmsApp/')) {
            throw new Error(`Blocked non-CMS script URL: ${rawUrl}`);
        }

        const safeUrl = parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;

        return trustedTypesPolicy ? trustedTypesPolicy.createScriptURL(safeUrl) : safeUrl;
    }

    /*----------------------------------------------------------------------
        Function: normalizeCmsPath
    ----------------------------------------------------------------------*/
    function normalizeCmsPath(assetPath) {
        // Resolve ./ and ../ segments without touching leading /cmsApp prefix logic.
        if (!assetPath) return assetPath;
        const parts = assetPath.replace(/\\/g, '/').split('/').filter(Boolean);
        const stack = [];
        for (const part of parts) {
            if (part === '.') continue;
            if (part === '..') {
                if (stack.length) stack.pop();
                continue;
            }
            stack.push(part);
        }
        return stack.join('/');
    }

    /*----------------------------------------------------------------------
        Function: normalizePublicJsFolder
    ----------------------------------------------------------------------*/
    function normalizePublicJsFolder(value) {
        // Normalize skip list folder keys: trim leading/trailing slashes.
        return value.replace(/^\/+|\/+$/g, '');
    }

    /*----------------------------------------------------------------------
        Function: loadBundleManifest
    ----------------------------------------------------------------------*/
    async function loadBundleManifest() {
        if (bundleManifest.promise) return bundleManifest.promise;
        bundleManifest.promise = (async () => {
            try {
                const url = `/${window.cmsRoutes.appDirName}/public/js/cms-bundles.json`;
                const res = await fetch(url, { cache: 'no-cache', credentials: 'same-origin' });
                if (!res.ok) return null;
                bundleManifest.data = await res.json();
                return bundleManifest.data;
            } catch (err) {
                if (window.CMS_ASSET_DEBUG === true) {
                    console.warn('[cmsPluginLoader] bundle manifest not loaded', err);
                }
                return null;
            }
        })();
        return bundleManifest.promise;
    }

    /*----------------------------------------------------------------------
        Function: getBundleManifestEntry
    ----------------------------------------------------------------------*/
    async function getBundleManifestEntry(namespace) {
        if (!namespace) return null;
        const data = await loadBundleManifest();
        return data?.modules?.[namespace] || null;
    }

    /*----------------------------------------------------------------------
        Function: loadPublicJsExternals
    ----------------------------------------------------------------------*/
    async function loadPublicJsExternals() {
        if (publicJsSkipPromise) return publicJsSkipPromise;
        publicJsSkipPromise = (async () => {
            try {
                // Fetch skip list once per page load.
                const url = `/${window.cmsRoutes.appDirName}/public/js/cms-externals.json`;
                const res = await fetch(url, { cache: 'no-cache', credentials: 'same-origin' });

                if (!res.ok) return;
                const data = await res.json();

                const list = Array.isArray(data?.publicJsSkip) ? data.publicJsSkip : Array.isArray(data?.public_js_skip) ? data.public_js_skip : [];
                list.forEach((item) => {
                    if (typeof item === 'string' && item.trim()) {
                        publicJsSkip.add(normalizePublicJsFolder(item.trim()));
                    }
                });
            } catch (err) {
                if (window.CMS_ASSET_DEBUG === true) {
                    console.warn('[cmsPluginLoader] public js externals not loaded', err);
                }
            }
        })();
        return publicJsSkipPromise;
    }

    /*----------------------------------------------------------------------
        Function: deriveBundlePath
    ----------------------------------------------------------------------*/
    function deriveBundlePath(namespace, meta) {
        const ns = typeof namespace === 'string' && namespace ? namespace : meta?.namespace;
        // Only module namespaces get implicit bundles; others must opt-in via meta.bundle.
        if (!ns || !ns.startsWith('modules/')) {
            return meta?.bundle ? meta.bundle : null;
        }
        // Only return a bundle path when the module has JS assets.
        const hasDepends = Array.isArray(meta?.dependsSelf?.js) && meta.dependsSelf.js.length > 0;
        const hasLoadJs = Array.isArray(meta?.load) && meta.load.includes('js');
        const hasJs = typeof meta?.js === 'string' && meta.js.trim().length > 0;
        if (!hasDepends && !hasLoadJs && !hasJs) return null;
        if (meta?.bundle) return meta.bundle;

        return `${ns}/assets/js/bundle-min.js`;
    }

    /*----------------------------------------------------------------------
        Function: preloadCss
    ----------------------------------------------------------------------*/
    function preloadCss(cssPath) {
        // Preload CSS to improve render timing without blocking.
        const normalized = normalizeCmsPath(cssPath);
        const fullPath = `/${window.cmsRoutes.appDirName}/${normalized}`;
        if (preloadedCss.has(fullPath)) return;
        preloadedCss.add(fullPath);
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'style';
        link.href = withBuildVersion(fullPath);
        document.head.appendChild(link);
    }

    /*----------------------------------------------------------------------
        Function: loadCss
    ----------------------------------------------------------------------*/
    function loadCss(cssPath) {
        // Inject CSS once per asset path.
        const normalized = normalizeCmsPath(cssPath);
        const fullPath = `/${window.cmsRoutes.appDirName}/${normalized}`;
        if (loadedCss.has(fullPath)) return Promise.resolve();
        if (loadingCss.has(fullPath)) return loadingCss.get(fullPath);
        loadedCss.add(fullPath);
        const ready = new Promise((resolve, reject) => {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = withBuildVersion(fullPath); // see #2
            link.onload = () => {
                loadingCss.delete(fullPath);
                resolve();
            };
            link.onerror = () => {
                loadingCss.delete(fullPath);
                reject(new Error(`Failed to load ${cssPath}`));
            };
            document.head.appendChild(link);
        });
        loadingCss.set(fullPath, ready);
        return ready;
    }

    /*----------------------------------------------------------------------
        Function: preloadScript
    ----------------------------------------------------------------------*/
    function preloadScript(path) {
        // Preload JS so it can execute later without blocking.
        const normalized = normalizeCmsPath(path);
        const fullPath = `/cmsApp/${normalized}`;
        if (preloadedJs.has(fullPath)) return;
        preloadedJs.add(fullPath);
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'script';
        link.href = withBuildVersion(fullPath);
        document.head.appendChild(link);
    }

    /*----------------------------------------------------------------------
        Function: loadScript
    ----------------------------------------------------------------------*/
    function loadScript(path) {
        // Load JS sequentially to preserve dependsSelf ordering.
        const normalized = normalizeCmsPath(path);
        const fullPath = `/cmsApp/${normalized}`;
        if (loadedJs.has(fullPath)) return Promise.resolve();
        loadedJs.add(fullPath);
        return new Promise((resolve, reject) => {
            const s = document.createElement('script');
            applyScriptNonce(s);
            s.src = toTrustedScriptURL(withBuildVersion(fullPath));
            s.async = false; // preserve order for self deps
            s.onload = resolve;
            s.onerror = () => reject(`Failed to load ${path}`);
            document.head.appendChild(s);
        });
    }

    /*----------------------------------------------------------------------
        Function: normalizeGlobalShim
    ----------------------------------------------------------------------*/
    function normalizeGlobalShim(raw) {
        if (!raw || typeof raw !== 'object') return null;
        const globalName = typeof raw.global === 'string' ? raw.global.trim() : '';
        if (!globalName) return null;

        const namespaces = [];
        const addNamespace = (value) => {
            if (typeof value === 'string' && value.trim()) namespaces.push(value.trim());
        };
        addNamespace(raw.namespace);
        if (Array.isArray(raw.namespaces)) raw.namespaces.forEach(addNamespace);

        const exportNames = [];
        const addExportName = (value) => {
            if (typeof value === 'string' && value.trim()) exportNames.push(value.trim());
        };
        addExportName(raw.exportName);
        if (Array.isArray(raw.exportNames)) raw.exportNames.forEach(addExportName);

        return { globalName, namespaces, exportNames };
    }

    /*----------------------------------------------------------------------
        Function: addGlobalShim
    ----------------------------------------------------------------------*/
    function addGlobalShim(raw) {
        const shim = normalizeGlobalShim(raw);
        if (!shim) return;
        const key = `${shim.globalName}::${shim.namespaces.join('|')}`;
        if (globalShimsState.keys.has(key)) return;
        globalShimsState.keys.add(key);
        globalShimsState.list.push(shim);
    }

    /*----------------------------------------------------------------------
        Function: loadGlobalShimsFromWindow
    ----------------------------------------------------------------------*/
    function loadGlobalShimsFromWindow() {
        if (globalShimsState.loaded) return;
        // Built-in shims for bundled externals.
        addGlobalShim({ namespace: 'shared/Quill', global: 'Quill' });

        const userShims = window.CMS_GLOBAL_SHIMS;
        if (Array.isArray(userShims)) {
            userShims.forEach(addGlobalShim);
        }

        globalShimsState.loaded = true;
    }

    /*----------------------------------------------------------------------
        Function: registerGlobalShim
    ----------------------------------------------------------------------*/
    function registerGlobalShim(raw) {
        loadGlobalShimsFromWindow();
        addGlobalShim(raw);
    }

    /*----------------------------------------------------------------------
        Function: ensureGlobalExport
    ----------------------------------------------------------------------*/
    function ensureGlobalExport(globalName, exportNames = []) {
        if (!globalName) return false;
        if (typeof window[globalName] !== 'undefined') return true;

        const names = Array.from(new Set([...(Array.isArray(exportNames) ? exportNames : []), globalName, 'default'])).filter(Boolean);
        const containers = [];
        if (window.exports) containers.push(window.exports);
        if (window.module && window.module.exports) containers.push(window.module.exports);

        for (const container of containers) {
            if (names.includes('*')) {
                window[globalName] = container;
                return true;
            }

            for (const name of names) {
                if (name === 'default' && typeof container === 'function') {
                    window[globalName] = container;
                    return true;
                }
                if (container && typeof container === 'object' && Object.prototype.hasOwnProperty.call(container, name)) {
                    window[globalName] = container[name];
                    return true;
                }
            }
        }

        return false;
    }

    /*----------------------------------------------------------------------
        Function: ensureGlobalShimsForNamespaces
    ----------------------------------------------------------------------*/
    function ensureGlobalShimsForNamespaces(namespaces) {
        loadGlobalShimsFromWindow();
        if (!globalShimsState.list.length) return;
        const nsSet = new Set(Array.isArray(namespaces) ? namespaces.filter(Boolean) : []);

        globalShimsState.list.forEach((shim) => {
            if (shim.namespaces.length) {
                const hasMatch = shim.namespaces.some((ns) => nsSet.has(ns));
                if (!hasMatch) return;
            }
            ensureGlobalExport(shim.globalName, shim.exportNames);
        });
    }

    /*----------------------------------------------------------------------
        Section: Bundle mode + script selection
    ----------------------------------------------------------------------*/
    /*----------------------------------------------------------------------
        Function: shouldUseBundles
    ----------------------------------------------------------------------*/
    function shouldUseBundles(namespace) {
        // Only backend module namespaces are bundled in production mode.
        const mode = (window.CMS_ASSET_MODE || '').toString().toLowerCase();
        if (mode === 'bundle') {
            return typeof namespace === 'string' && namespace.startsWith('modules/');
        }
        if (mode === 'individual' || mode === 'files') {
            return false;
        }
        return false;
    }

    /*----------------------------------------------------------------------
        Function: isBundledNamespace
    ----------------------------------------------------------------------*/
    function isBundledNamespace(namespace) {
        // Marks namespaces that may be bundled into a module output.
        return typeof namespace === 'string' && (namespace.startsWith('modules/') || namespace.startsWith('shared/') || namespace.startsWith('record_manager/'));
    }

    /*----------------------------------------------------------------------
        Function: getPublicJsFolder
    ----------------------------------------------------------------------*/
    function getPublicJsFolder(assetPath) {
        // Extract the first folder under public/js for skip matching.
        if (!assetPath) return null;
        const normalized = assetPath.replace(/\\/g, '/');
        const token = 'public/js/';
        const idx = normalized.indexOf(token);
        if (idx === -1) return null;
        const rest = normalized.slice(idx + token.length);
        if (!rest) return null;
        return rest.split('/')[0] || null;
    }

    /*----------------------------------------------------------------------
        Function: shouldSkipPublicJs
    ----------------------------------------------------------------------*/
    function shouldSkipPublicJs(assetPath) {
        // Skip folders that are flagged as external in cms-externals.json.
        const folder = getPublicJsFolder(assetPath);
        return folder ? publicJsSkip.has(folder) : false;
    }

    /*----------------------------------------------------------------------
        Function: isExternalScriptPath
    ----------------------------------------------------------------------*/
    function isExternalScriptPath(assetPath) {
        // Node/composer scripts are always treated as external.
        if (!assetPath) return false;
        const normalized = assetPath.replace(/\\/g, '/');
        if (normalized.includes('node_modules/')) return true;
        if (normalized.includes('composer_modules/')) return true;
        return false;
    }

    /*----------------------------------------------------------------------
        Function: shouldLoadScript
    ----------------------------------------------------------------------*/
    function shouldLoadScript(assetPath, useBundle, skipBundledJs) {
        // In bundle mode, only load external scripts; in file mode load everything.
        if (!assetPath) return false;
        if (shouldSkipPublicJs(assetPath)) return false;
        if (!useBundle && !skipBundledJs) return true;
        return isExternalScriptPath(assetPath);
    }

    /*----------------------------------------------------------------------
        Function: preloadExternalScriptsForMod
    ----------------------------------------------------------------------*/
    function preloadExternalScriptsForMod(mod) {
        // Preload external scripts (node/composer/public skip list) for this module.
        if (!mod) return;
        if (mod.self && Array.isArray(mod.self.js) && mod.self.js.length) {
            mod.self.js.forEach((asset) => {
                if (shouldLoadScript(asset, true, true)) preloadScript(asset);
            });
        }
        if (shouldLoadScript(mod.js, true, true)) preloadScript(mod.js);
    }

    /*----------------------------------------------------------------------
        Function: loadExternalScriptsForMod
    ----------------------------------------------------------------------*/
    async function loadExternalScriptsForMod(mod) {
        // Load externals before bundle so globals exist when bundle executes.
        if (!mod) return;
        if (mod.self && Array.isArray(mod.self.js) && mod.self.js.length) {
            for (const asset of mod.self.js) {
                if (shouldLoadScript(asset, true, true)) {
                    await loadScript(asset);
                }
            }
        }
        if (shouldLoadScript(mod.js, true, true)) {
            await loadScript(mod.js);
        }
    }

    /*----------------------------------------------------------------------
        Section: Dependency graph + module loading
    ----------------------------------------------------------------------*/
    /*----------------------------------------------------------------------
        Function: collectDependenciesOrdered
    ----------------------------------------------------------------------*/
    function collectDependenciesOrdered(name, ordered, visiting = new Set(), visited = new Set()) {
        // Depth-first traversal to build a load order without duplicates.
        const mod = __cmsPluginRegistry[name];
        if (!mod) return;
        if (visiting.has(name)) return;
        if (visited.has(name)) return;
        visiting.add(name);
        if (Array.isArray(mod.depends)) {
            for (const dep of mod.depends) {
                collectDependenciesOrdered(dep, ordered, visiting, visited);
            }
        }
        visiting.delete(name);
        visited.add(name);
        if (name !== ordered.root) ordered.push(name);
    }

    const bundledDeps = new Set();

    /*----------------------------------------------------------------------
        Function: loadModule
    ----------------------------------------------------------------------*/
    async function loadModule(name, trackingSet = new Set(), opts = {}) {
        await loadPublicJsExternals();
        const mod = __cmsPluginRegistry[name];
        if (!mod) return;
        if (mod.loading) return await mod.loading;

        mod.loading = (async () => {
            // Prevent circular dependencies during a single load chain.
            if (trackingSet.has(name)) throw new Error(`Circular dependency detected: ${name}`);
            trackingSet.add(name);

            const namespaceKey = mod.namespace || name;
            const wantsBundle = shouldUseBundles(namespaceKey);
            const manifestEntry = wantsBundle ? await getBundleManifestEntry(namespaceKey) : null;
            const bundlePath = (manifestEntry && manifestEntry.bundle) || mod.bundleJs || deriveBundlePath(namespaceKey, mod);
            const useBundle = wantsBundle && !!bundlePath;
            const skipBundledJs = bundledDeps.has(namespaceKey);
            if (window.CMS_ASSET_DEBUG === true) {
                console.log('[cmsPluginLoader] module', namespaceKey, 'bundle:', useBundle, 'skipBundledJs:', skipBundledJs);
            }

            if (wantsBundle && !manifestEntry) {
                throw new Error(`Bundle manifest missing entry for ${namespaceKey}`);
            }

            // Preload all assets first to improve load parallelism.
            if (!useBundle) {
                if (mod.self && Array.isArray(mod.self.css) && mod.self.css.length) {
                    mod.self.css.forEach(preloadCss);
                }
                if (mod.self && Array.isArray(mod.self.js) && mod.self.js.length) {
                    mod.self.js.forEach((asset) => {
                        if (shouldLoadScript(asset, useBundle, skipBundledJs)) preloadScript(asset);
                    });
                }
                if (mod.css) preloadCss(mod.css);
                if (shouldLoadScript(mod.js, useBundle, skipBundledJs)) preloadScript(mod.js);
            } else if (useBundle) {
                if (mod.self && Array.isArray(mod.self.js) && mod.self.js.length) {
                    mod.self.js.forEach((asset) => {
                        if (shouldLoadScript(asset, useBundle, skipBundledJs)) preloadScript(asset);
                    });
                }
                preloadScript(bundlePath);
                const manifestCss = Array.isArray(manifestEntry?.css) ? manifestEntry.css : null;
                if (manifestCss && manifestCss.length) {
                    manifestCss.forEach(preloadCss);
                } else if (mod.css) {
                    preloadCss(mod.css);
                }
            }

            // In file mode, load dependencies before this module.
            if (!useBundle && !opts.skipDepends && Array.isArray(mod.depends)) {
                for (const dep of mod.depends) {
                    await loadModule(dep, trackingSet);
                }
            }

            // Load css/js for individual mode in declared order.
            if (!useBundle) {
                if (mod.self && Array.isArray(mod.self.css) && mod.self.css.length) {
                    await Promise.all(mod.self.css.map(loadCss));
                }
                if (mod.self && Array.isArray(mod.self.js) && mod.self.js.length) {
                    for (const js of mod.self.js) {
                        if (shouldLoadScript(js, useBundle, skipBundledJs)) {
                            await loadScript(js);
                        }
                    }
                }
            }

            if (useBundle) {
                const manifestCss = Array.isArray(manifestEntry?.css) ? manifestEntry.css : null;
                if (manifestCss && manifestCss.length) {
                    await Promise.all(manifestCss.map(loadCss));
                } else if (mod.css) {
                    await loadCss(mod.css);
                }
            } else if (mod.css) {
                await loadCss(mod.css);
            }

            if (useBundle) {
                try {
                    if (window.CMS_ASSET_DEBUG === true) {
                        console.log('[cmsPluginLoader] loading bundle', bundlePath);
                    }
                    // Load externals for the full dependency graph first.
                    const depsOrdered = [];
                    depsOrdered.root = name;
                    collectDependenciesOrdered(name, depsOrdered);
                    const externalTargets = [name, ...depsOrdered];
                    const externalJs = Array.isArray(manifestEntry?.externalJs) ? manifestEntry.externalJs : null;
                    if (externalJs && externalJs.length) {
                        externalJs.forEach(preloadScript);
                        for (const asset of externalJs) {
                            await loadScript(asset);
                        }
                    } else {
                        externalTargets.forEach((depName) => preloadExternalScriptsForMod(__cmsPluginRegistry[depName]));
                        for (const depName of externalTargets) {
                            await loadExternalScriptsForMod(__cmsPluginRegistry[depName]);
                        }
                    }
                    ensureGlobalShimsForNamespaces(externalTargets);
                    // Then execute the bundle.
                    await loadScript(bundlePath);
                    if (isBundledNamespace(name)) bundledDeps.add(name);
                    depsOrdered.forEach((dep) => {
                        if (isBundledNamespace(dep)) bundledDeps.add(dep);
                    });
                    // Register dependency modules as loaded without reloading their assets.
                    for (const dep of depsOrdered) {
                        await loadModule(dep, new Set(), { skipDepends: true });
                    }
                } catch (err) {
                    if (window.CMS_ASSET_DEBUG === true) {
                        console.warn('[cmsPluginLoader] bundle failed, falling back', err);
                    }
                    if (Array.isArray(mod.depends)) {
                        for (const dep of mod.depends) {
                            await loadModule(dep, trackingSet);
                        }
                    }
                    if (mod.self && Array.isArray(mod.self.js) && mod.self.js.length) {
                        for (const js of mod.self.js) {
                            if (shouldLoadScript(js, useBundle, skipBundledJs)) {
                                await loadScript(js);
                            }
                        }
                    }
                    if (shouldLoadScript(mod.js, useBundle, skipBundledJs)) await loadScript(mod.js);
                }
            } else if (shouldLoadScript(mod.js, useBundle, skipBundledJs)) {
                await loadScript(mod.js);
            }

            let oInstance = null;
            const publishName = mod.publishName;

            // If publishName is provided, instantiate or call init and store on window.
            if (publishName) {
                const C = window[publishName];
                if (typeof C === 'function' && mod.selfInit) {
                    oInstance = new C();
                }
            }

            if (!oInstance && typeof mod.init === 'function') {
                oInstance = await mod.init();
            }

            // Store ready promises for both namespace and publishName keys.
            if (publishName && oInstance) {
                window[publishName] = oInstance;
            }

            const ready = Promise.resolve(oInstance);
            window.__cmsPluginsReady.set(name, ready);
            if (publishName) window.__cmsPluginsReady.set(publishName, ready);

            return oInstance;
        })();

        try {
            return await mod.loading;
        } finally {
            mod.loading = null;
            trackingSet.delete(name);
        }
    }

    /*----------------------------------------------------------------------
        Function: loadCmsModulesByGroup
    ----------------------------------------------------------------------*/
    async function loadCmsModulesByGroup(jSonMeta) {
        await loadPublicJsExternals();
        const route = jSonMeta.route;
        const namespace = jSonMeta.namespace;
        registerCmsPlugin(namespace, {});

        const entry = __cmsPluginRegistry[namespace];
        entry.namespace = namespace;
        entry.bundleJs = deriveBundlePath(namespace, jSonMeta);

        const useBundle = shouldUseBundles(namespace) && !!entry.bundleJs;

        // In file mode, load dependsSelf JS that lives under assets/js.
        if (!useBundle && Array.isArray(jSonMeta?.dependsSelf?.js)) {
            jSonMeta.dependsSelf.js.forEach((item) => {
                const asset = `${namespace}/assets/js/${item}`;
                if (shouldLoadScript(asset, useBundle, false)) preloadScript(asset);
            });
            for (const item of jSonMeta.dependsSelf.js) {
                const asset = `${namespace}/assets/js/${item}`;
                if (shouldLoadScript(asset, useBundle, false)) {
                    await loadScript(asset);
                }
            }
        }

        // Extra external module scripts (legacy support).
        if (Array.isArray(jSonMeta?.dependsExternalModules?.js)) {
            jSonMeta.dependsExternalModules.js.forEach((item) => preloadScript(`modules/${item}`));
            for (const item of jSonMeta.dependsExternalModules.js) {
                await loadScript(`modules/${item}`);
            }
        }

        // load array indicates standard asset naming.
        if (jSonMeta.load && jSonMeta.load.find((item) => item === 'js') === 'js') {
            entry.js = `${namespace}/assets/js/${route}-min.js`;
        }

        if (jSonMeta.load && jSonMeta.load.find((item) => item === 'css') === 'css') {
            entry.css = `${namespace}/assets/css/${route}.css`;
        }

        // Attach depends list if present.
        if (jSonMeta.depends !== undefined) {
            entry.depends = jSonMeta.depends;
        }

        entry.hasIntents = false;

        if (jSonMeta.intents !== undefined) {
            entry.hasIntents = true;
            entry.intents = jSonMeta.intents;
        }

        entry.publishName = `${jSonMeta.root}_${route}`;
        // Default init for module metadata.
        entry.init = () => new window[route]();

        await loadModule(namespace);
    }

    /*----------------------------------------------------------------------
        Function: fetchWidgetRegister
    ----------------------------------------------------------------------*/
    async function fetchWidgetRegister(widgets) {
        try {
            // Pull widget registry to determine frontend dependencies.
            const registerObj = await window.cmsGlobal.fetchJson(window.cmsRoutes.apiWidgetRegister, {
                lCheckSession: false,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Cms-Internal-Token': window.cmsInternalToken
                },
                cache: 'no-cache',
                credentials: 'same-origin',
                body: JSON.stringify({ cms_internal: '1' })
            });

            const register = Object.values(registerObj || {});
            const index = new Map();
            const frontendDepends = new Set();
            // Build lookup for widget meta + collect frontend deps.
            register.forEach((item) => {
                const meta = item?.meta;
                if (!meta?.key || !meta?.namespace) return;

                index.set(`${meta.namespace}::${meta.key}`, item);
            });

            // Merge depends from live widget instances.
            Object.values(widgets || {}).forEach((widget) => {
                const key = widget?.type;
                const ns = widget?.namespace;

                if (!key || !ns) return;

                const match = index.get(`${ns}::${key}`);

                if (match && Array.isArray(match.meta.frontend_depends)) {
                    match.meta.frontend_depends.forEach((dep) => frontendDepends.add(dep));
                }
            });

            return Array.from(frontendDepends);
        } catch (err) {
            window.cmsGlobal.toastError(`fetchWidgetRegister(): ${err.message}`);
            return [];
        }
    }

    /*----------------------------------------------------------------------
        Function: normalizeScopedThemeCss
        Purpose: widget-scoped theme CSS cannot safely keep `html { font-size }`
                 because it leaks globally. We remove html blocks, then scale
                 rem tokens so typography keeps the intended pixel result.
    ----------------------------------------------------------------------*/
    function normalizeScopedThemeCss(css = '', selectorList = '') {
        if (typeof css !== 'string' || css.length === 0) return '';

        const htmlBaseRegex = /html\s*\{[^}]*font-size\s*:\s*([0-9]*\.?[0-9]+)px[^}]*\}/i;
        const match = css.match(htmlBaseRegex);
        const basePx = match ? Number(match[1]) : null;

        // Remove any html {} blocks from scoped theme payloads.
        let normalized = css.replace(/html\s*\{[^}]*\}/gi, '');

        if (Number.isFinite(basePx) && basePx > 0 && basePx !== 16) {
            const remScale = basePx / 16;
            normalized = normalized.replace(/(-?\d*\.?\d+)rem\b/g, (full, num) => {
                const parsed = Number(num);
                if (!Number.isFinite(parsed)) return full;
                const scaled = Number((parsed * remScale).toFixed(4));
                return `${scaled}rem`;
            });
        }

        // Scope vars to the widget/container selectors assembled by loader.
        return normalized.replaceAll(':root', selectorList);
    }

    /*----------------------------------------------------------------------
        Function: loadFrontend
    ----------------------------------------------------------------------*/
    async function loadFrontend() {
        // Read the widgets that the frontend has registered on the page.
        const widgets = window.cmsFrontedWidgets;
        // Ask the backend/widget registry which plugin modules those widgets depend on.
        const frontendDepends = await fetchWidgetRegister(widgets);
        for (const dep of frontendDepends) {
            // Load each dependent module before we try to render/apply widget assets.
            await loadModule(dep);
        }

        // Group widget selectors by CSS file so the same stylesheet is fetched only once.
        const stylesheetSelectors = new Map();
        // Collect unique frontend JS files from the widget payload.
        const scriptPaths = new Set();

        // Dont Load Default typescript as the index.php already does this.
        const cDefaultTypeScale = `TypeScaleJsonBuilder/themes/${window.__THEME_MASTER_CMS_NAMESPACE__}`;
        Object.values(widgets || {}).forEach((group) => {
            if (!Array.isArray(group.widgets) || !group.widgets.length) return;
            group.widgets.forEach((item) => {
                const selector = group.type === 'container' ? `[data-container-id="${item.widget_id}"]` : `[data-widget-id="${item.widget_id}"]`;
                const el = document.querySelector(selector);
                if (el && Array.isArray(item.css) && item.css.length) {
                    item.css.forEach((entry) => {
                        const href = typeof entry === 'string' ? entry : entry.filepath;

                        const defaultCss = entry?.default ?? false;
                        if (!href) return;
                        if (defaultCss) return;
                        if (href.includes(cDefaultTypeScale)) return;
                        if (!stylesheetSelectors.has(href)) stylesheetSelectors.set(href, new Set());
                        stylesheetSelectors.get(href).add(selector);
                    });
                }

                if (!Array.isArray(item.js) || !item.js.length) return;

                item.js.forEach((entry) => {
                    const path = typeof entry === 'string' ? entry : entry.filepath;
                    if (typeof path === 'string' && path.trim()) {
                        scriptPaths.add(path.trim());
                    }
                });
            });
        });

        const cssText = await Promise.all(
            Array.from(stylesheetSelectors.entries()).map(async ([href, selectors]) => {
                try {
                    const response = await fetch(href);
                    const css = await response.text();
                    const selectorList = Array.from(selectors).join(',\n');
                    return normalizeScopedThemeCss(css, selectorList);
                } catch (err) {
                    if (window.CMS_ASSET_DEBUG === true) {
                        console.warn('[cmsPluginLoader] frontend widget css failed to load', href, err);
                    }
                    return '';
                }
            })
        );

        if (cssText.some(Boolean)) {
            const style = document.createElement('style');
            style.textContent = cssText.join('\n');
            document.head.appendChild(style);
        }

        for (const path of scriptPaths) {
            const normalized = normalizeCmsPath(path);
            const fullPath = path.startsWith('/') || path.startsWith('./') || path.startsWith('../') || /^[a-z]+:/i.test(path) ? path : `/${window.cmsRoutes.appDirName}/${normalized}`;

            if (loadedJs.has(fullPath)) continue;
            loadedJs.add(fullPath);

            await new Promise((resolve, reject) => {
                const script = document.createElement('script');
                applyScriptNonce(script);
                script.src = toTrustedScriptURL(withBuildVersion(fullPath));
                script.async = false;
                script.onload = resolve;
                script.onerror = () => reject(new Error(`Failed to load ${path}`));
                document.head.appendChild(script);
            });
        }
    }

    /*----------------------------------------------------------------------
        Function: setAppContextFrontend
    ----------------------------------------------------------------------*/
    function setAppContextFrontend() {
        // Switch asset load rules to frontend mode.
        APP_CONTEXT = 'frontend';
    }

    /*----------------------------------------------------------------------
        Function: registerCmsPlugin
    ----------------------------------------------------------------------*/
    function registerCmsPlugin(name, definition) {
        // Low-level registry insert.
        __cmsPluginRegistry[name] = definition;
    }

    /*----------------------------------------------------------------------
        Section: Public API
    ----------------------------------------------------------------------*/
    window.__cmsSystem = {
        setAppContentFrontend: setAppContextFrontend,
        init: loadCmsModulesByGroup,
        initFrontend: loadFrontend,
        load: fetchPlugins,
        register: registerCmsPlugin,
        registerGlobalShim,
        ready: (key) => window.__cmsPluginsReady.get(key)
    };
})();
